'use client';

import { HeBtn } from '@/components/mh/HeBtn';
import { hubCopy } from '@/lib/hub/copy';
import { useHubData } from '../../_lib/use-hub-data';
import type {
  GrowthGift,
  GrowthInsight,
  GrowthJourney,
  GrowthKpis,
  HunterNote,
  Kpi,
} from '@/lib/hub/types';

function Spark({ points }: { points: number[] }) {
  if (!points.length) return null;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const coords = points
    .map((p, i) => {
      const x = (i / (points.length - 1 || 1)) * 100;
      const y = 22 - ((p - min) / range) * 20 - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <div className="kpi-spark">
      <svg viewBox="0 0 100 22" preserveAspectRatio="none">
        <polyline points={coords} fill="none" stroke="#0F1011" strokeWidth={1.25} strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function KpiCard({ label, kpi }: { label: string; kpi: Kpi }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{kpi.value}</div>
      {kpi.sparkline.length ? <Spark points={kpi.sparkline} /> : null}
      {kpi.delta ? <div className="kpi-delta muted">{kpi.delta}</div> : null}
    </div>
  );
}

const TREND_COPY = hubCopy().trend;

export function Growth() {
  const kpis = useHubData<GrowthKpis>('/api/growth/kpis');
  const journey = useHubData<GrowthJourney>('/api/growth/journey?range=this_month');
  const insights = useHubData<GrowthInsight[]>('/api/growth/insights');
  const gift = useHubData<GrowthGift>('/api/growth/gift?range=this_week');
  const note = useHubData<HunterNote>('/api/hub/hunter-note?surface=growth');

  const insightItems = insights.data ?? [];

  return (
    <div className="hub-shell">
      <div className="hub-col-tabbed">
        <div className="hub-hero">
          <h1 className="t">How your business is doing.</h1>
          <p className="s">This month so far. Hunter watches every channel.</p>
        </div>

        {/* KPIs */}
        {kpis.data ? (
          <div className="kpi-grid">
            <KpiCard label="Money in" kpi={kpis.data.revenue} />
            <KpiCard label="New people interested" kpi={kpis.data.leads} />
            <KpiCard label="Campaigns running" kpi={kpis.data.campaigns_running} />
            <KpiCard label="Of every 100 new people, this many became customers" kpi={kpis.data.customers_per_100} />
          </div>
        ) : (
          <div className="hub-skeleton">Loading…</div>
        )}

        {/* Journey */}
        <div className="h2-eyebrow"><span>This month, how it went</span></div>
        {journey.data && (journey.data.heard + journey.data.wrote + journey.data.bought) > 0 ? (
          <div className="jn-card">
            <p className="jn-header">A simple story of the month so far.</p>
            <div className="jn-row"><div className="jn-num">{journey.data.heard}</div><div className="jn-label">people heard about you</div><span /></div>
            <div className="jn-row"><div className="jn-num">{journey.data.wrote}</div><div className="jn-label">of them wrote you</div><span /></div>
            <div className="jn-row"><div className="jn-num">{journey.data.interested}</div><div className="jn-label">said they were interested</div><span /></div>
            <div className="jn-row"><div className="jn-num">{journey.data.bought}</div><div className="jn-label">became customers</div><span /></div>
            <p className="jn-foot">{TREND_COPY[journey.data.trend]}</p>
          </div>
        ) : (
          <div className="hub-empty">Not enough yet to see the shape of things.</div>
        )}

        {/* Insights */}
        <div className="h2-eyebrow">
          <span>Things Hunter noticed this week</span>
          {insightItems.length > 0 ? <span className="right">{insightItems.length} noticings</span> : null}
        </div>
        {insights.isLoading ? (
          <div className="hub-skeleton">Loading…</div>
        ) : insightItems.length === 0 ? (
          <div className="hub-empty">Hunter hasn’t noticed anything new this week.</div>
        ) : (
          <div className="in-grid">
            {insightItems.map((it) => (
              <div key={it.id} className="in-card">
                <div className="in-copy">{it.observation}</div>
                <div className="in-actions">
                  {it.primary_action ? (
                    <HeBtn
                      label={`Review & ${it.primary_action.label}`}
                      onPrimary={() => {
                        window.location.href = it.primary_action!.url;
                      }}
                      options={[
                        {
                          id: 'review',
                          label: `Review & ${it.primary_action.label}`,
                          onSelect: () => {
                            window.location.href = it.primary_action!.url;
                          },
                        },
                        { id: 'later', label: 'Remind me later', onSelect: () => {} },
                        { id: 'no', label: 'No thanks', onSelect: () => {} },
                      ]}
                    />
                  ) : null}
                  {it.secondary_action ? <button className="in-btn">{it.secondary_action.label}</button> : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Gift */}
        <div className="h2-eyebrow"><span>This week Hunter helped</span></div>
        {gift.data && gift.data.hours_handled > 0 ? (
          <div className="rr-card">
            <p className="rr-copy">
              This week Hunter handled about <strong>{gift.data.hours_handled} hours</strong>
              {gift.data.revenue_brought ? <> and brought in <strong>${gift.data.revenue_brought.toLocaleString('en-US')}</strong></> : null}.
            </p>
            <p className="rr-sub">That’s time back in your week.</p>
          </div>
        ) : (
          <div className="hub-empty">Hunter is just getting going. This grows fast.</div>
        )}

        {note.data ? (
          <div className="hn-foot">
            {note.data.em
              ? note.data.text.split('{em}').map((part, i, arr) => (
                  <span key={i}>{part}{i < arr.length - 1 ? <em>{note.data!.em}</em> : null}</span>
                ))
              : note.data.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}
